import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  APPLICATION_STATUS_COLOR,
  pill,
  formatDateTime,
  tableHeadCellSx,
  tableBodyCellSx,
} from "bundles/Internal/components/CompanyShowPage";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

function CompanyJobApplicationsTab({ jobApplications, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Stack spacing={1} sx={{ p: 4 }}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (!jobApplications || jobApplications.length === 0) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
          No job applications.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Id", "Candidate", "Email", "Job offer", "Status", "Source", "Created by", "Created"].map((label) => (
              <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {jobApplications.map((ja) => {
            const statusColor = APPLICATION_STATUS_COLOR[ja.status] || APPLICATION_STATUS_COLOR.pending;
            const candidate = ja.candidate;
            const candidateName = candidate
              ? [candidate.first_name, candidate.last_name].filter(Boolean).join(" ") || `#${candidate.id}`
              : "—";
            let source = "—";
            if (ja.sent_from_talent_team) source = "Talent team";
            else if (ja.added_from_cvtheque) source = "CV library";
            return (
              <TableRow
                key={ja.id}
                hover
                onClick={() => navigate(`/internal/job_applications/${ja.id}`)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={tableBodyCellSx}>{ja.id}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {candidate?.id ? (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/internal/candidates/${candidate.id}`);
                      }}
                      sx={{
                        color: "#079344",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {candidateName}
                    </Box>
                  ) : candidateName}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{candidate?.email || "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {ja.job_offer ? (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/internal/job_offers/${ja.job_offer.id}`);
                      }}
                      sx={{
                        color: "#079344",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {ja.job_offer.job_title || `#${ja.job_offer.id}`}
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {ja.status ? pill(ja.status, statusColor) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{source}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {ja.created_by ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <UserAvatar user={ja.created_by} size={24} />
                      <span>{ja.created_by.full_name}</span>
                    </Box>
                  ) : "—"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDateTime(ja.created_at) || "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export default CompanyJobApplicationsTab;
